import React, {useCallback} from 'react';
import {makeStyles} from '@mui/styles';
import {useTranslation} from 'react-i18next';
import BglSimpleTextFieldSelect from '../ui-components/BglSimpleTextFieldSelect';
import {globalTheme} from '../theme/globalTheme';
import { Button, Hidden, TextField, Grid, Typography } from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {changeParams} from '../actions/actions';
import {fetchSimulation} from '../actions/async-actions';
import NumberFormat from 'react-number-format';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const currencies = ['CHF', 'CZK', 'DKK','GBP', 'HUF', 'ISK', 'NOK', 'PLN', 'RON', 'SEK'];

const theme = createTheme({
    ...globalTheme,
    components: {
        ...globalTheme.components,
        MuiFormControl: {
            styleOverrides: {
                root: {
                    ...globalTheme.components.MuiFormControl.styleOverrides.root,
                    backgroundColor: 'inherit'
                }
            }
        },
        MuiSelect: {
            styleOverrides: {
                icon: {
                    color: 'white'
                }
            }
        },
        MuiInput: {
            ...globalTheme.components.MuiInput,
            styleOverrides: {
                root: {
                    ...globalTheme.components.MuiInput.styleOverrides.root,
                    minHeight: 'auto'
                },
                input: {
                    ...globalTheme.components.MuiInput.styleOverrides.input,
                    fontSize: '18px',
                    color: 'white',
                    padding: '0',
                    height: '40px',
                    lineHeight: '40px'
                }
            }
        }
    }
});

const useStyles = makeStyles(themeToStyle => ({
    container: {
        backgroundColor: '#054a36',
        height: '100%',
        color: 'white',
        padding: '10% 5%',
        [themeToStyle.breakpoints.up('md')]: {
            padding: '10% 15%'
        }
    }
}));

const Panel = () => {
    const {t} = useTranslation(['fees-simulator']);
    const classes = useStyles();
    const dispatch = useDispatch();

    const paymentAmount = useSelector(reducer => reducer.appState.get('paymentAmount'));
    const cardType = useSelector(reducer => reducer.appState.get('cardType'));
    const paymentCurrency = useSelector(reducer => reducer.appState.get('paymentCurrency'));
    const changed = useSelector(reducer => reducer.appState.get('changed'));

    const handleFieldChange = useCallback(event => {
        dispatch(changeParams(event.target.name, event.target.value));
    }, [dispatch]);

    const makeSimulation = useCallback(() => {
        dispatch(fetchSimulation(paymentAmount, cardType, paymentCurrency));
    }, [dispatch, paymentAmount, cardType, paymentCurrency]);

    return (
        <div className={classes.container}>
            <ThemeProvider theme={theme}>
                <Grid container spacing={8} justify='flex-start' alignItems='baseline'>
                    <Grid item xs={12}>
                        <Typography variant='h5' gutterBottom>{t('panel.title')}</Typography>
                    </Grid>

                    <Grid item container spacing={7} justify='flex-start' alignItems='baseline'>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h6' gutterBottom>{t('details.amount-payment')}</Typography>
                            <TextField
                                variant='standard'
                                label=''
                                onChange={handleFieldChange}
                                name={'paymentAmount'}
                                value={paymentAmount}
                                InputProps={{
                                    inputComponent: NumberFormat,
                                    inputProps: {
                                        decimalSeparator: '.',
                                        allowNegative: false,
                                        isNumericString: true,
                                        decimalScale: 2,
                                        isAllowed: values => {
                                            const { formattedValue, floatValue } = values;
                                            return formattedValue === '' || (floatValue > 0 && floatValue <= 1000000000);
                                        }
                                    }
                                }}
                            />
                        </Grid>
                        <Grid item md={1} implementation='css' smDown component={Hidden}/>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h6' gutterBottom>{t('panel.currency-payment')}</Typography>
                            <BglSimpleTextFieldSelect
                                label=''
                                name={'paymentCurrency'}
                                onChange={handleFieldChange}
                                value={paymentCurrency}
                                itemLabels={currencies}
                                itemValues={currencies}/>
                        </Grid>
                        <Grid item md={1} implementation='css' smDown component={Hidden}/>
                        <Grid item xs={12} md={6}>
                            <Typography variant='h6' gutterBottom>{t('panel.card-type')}</Typography>
                            <BglSimpleTextFieldSelect
                                label=''
                                name={'cardType'}
                                onChange={handleFieldChange}
                                value={cardType}
                                itemLabels={['Mastercard', 'Visa', 'VPAY']}
                                itemValues={['MASTERCARD', 'VISA', 'VPAY']}/>
                        </Grid>
                    </Grid>

                    <Grid item xs={12}>
                        <Button
                            disabled={!changed}
                            variant={'contained'}
                            color={'primary'}
                            fullWidth
                            onClick={makeSimulation}
                        >
                            {t('calculate')}
                        </Button>
                    </Grid>
                </Grid>
            </ThemeProvider>
        </div>
    );
};

export default React.memo(Panel);
